import {
	UserIcon,
	CalendarIcon,
	ArrowRightIcon,
	ChevronRightIcon,
	ChartBarIcon,
	ChatBubbleLeftEllipsisIcon,
} from '@heroicons/react/20/solid';

import { formatDate } from '../../common/formatDateText';
import { DateRangeProgressBarCell } from '../../components/dateRangeProgressBar/DateRangeProgressBar';
import { Tooltip } from '../../components/smartTable/Tooltip';
import { StudentNotesPreview } from './StudentNotesPreview';

const normalizeValue = (value) => {
	if (value === null || value === undefined) return '';
	if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
		return String(value);
	}
	// Algunos registros legacy llegan como objetos tipo { $trim: 'texto' }.
	if (typeof value === 'object' && '$trim' in value) {
		const trimmed = value.$trim;
		return trimmed === null || trimmed === undefined ? '' : String(trimmed);
	}
	return '';
};

const normalizeArray = (value) => {
	if (!Array.isArray(value)) return [];
	return value.map((item) => normalizeValue(item)).filter(Boolean);
};

const StatusBadge = ({ value }) => {
	const safe = normalizeValue(value).toLowerCase();
	if (!safe) return null;
	const map = {
		activo: { wrap: 'bg-emerald-100 text-emerald-700 border-emerald-200', dotPing: 'bg-emerald-500' },
		suspendido: { wrap: 'bg-orange-100 text-orange-700 border-orange-200', dotPing: 'bg-orange-400' },
		inactivo: { wrap: 'bg-gray-100 text-gray-700 border-gray-200', dotPing: 'bg-gray-400' },
		baja: { wrap: 'bg-rose-100 text-rose-700 border-rose-200', dotPing: 'bg-rose-500' },
	};
	const cfg = map[safe];
	if (!cfg) return null;
	return (
		<span className={`inline-flex items-center justify-center gap-1.5 w-[110px] text-xs font-bold px-3 py-1.5 rounded-md border shadow-sm ${cfg.wrap}`}>
			<span className='relative flex h-2 w-2'>
				<span className={`animate-ping absolute inline-flex h-full w-full rounded-md opacity-75 ${cfg.dotPing}`}></span>
				<span className={`relative inline-flex rounded-md h-2 w-2 ${cfg.dotPing}`}></span>
			</span>
			{safe.toUpperCase()}
		</span>
	);
};

const SubscriptionBadge = ({ value }) => {
	const safe = normalizeValue(value).toLowerCase();
	if (!safe) return null;
	const map = {
		pagado: { wrap: 'bg-emerald-100 text-emerald-700 border-emerald-200', dotPing: 'bg-emerald-500' },
		inactivo: { wrap: 'bg-gray-100 text-gray-700 border-gray-200', dotPing: 'bg-gray-400' },
		vencida: { wrap: 'bg-rose-100 text-rose-700 border-rose-200', dotPing: 'bg-rose-500' },
	};
	const cfg = map[safe];
	if (!cfg) return null;
	return (
		<span className={`inline-flex items-center justify-center gap-1.5 w-[110px] text-xs font-bold px-3 py-1.5 rounded-md border shadow-sm ${cfg.wrap}`}>
			<span className='relative flex h-2 w-2'>
				<span className={`animate-ping absolute inline-flex h-full w-full rounded-md opacity-75 ${cfg.dotPing}`}></span>
				<span className={`relative inline-flex rounded-md h-2 w-2 ${cfg.dotPing}`}></span>
			</span>
			{safe.toUpperCase()}
		</span>
	);
};

/**
 * Construye las column defs (TanStack) para la tabla de estudiantes.
 *
 * `meta.excelHeaders`: claves del Excel en el servidor (solo columnas exportables).
 * `meta.enableExcel: false`: la columna puede mostrarse en la tabla pero no se envía al Excel.
 *
 * @param {{ onOpenProfile: (id: string) => void }} options
 */
export const buildStudentsColumns = ({ onOpenProfile }) => [
	{
		accessorKey: 'matricula',
		id: 'matricula',
		header: 'Matrícula',
		size: 130,
		cell: ({ getValue }) => (
			<p className='w-[80px] text-center border border-gray-300 rounded-md px-2 py-1 text-gray-700 font-semibold'>
				{normalizeValue(getValue())}
			</p>
		),
		meta: { label: 'Matrícula', searchable: true, searchPlaceholder: 'Buscar matrícula...', excelHeaders: ['Matricula'] },
	},
	{
		accessorKey: 'nameUser',
		id: 'nameUser',
		header: 'Estudiante',
		size: 360,
		cell: ({ getValue, row }) => {
			const name = normalizeValue(getValue());
			const email = normalizeValue(row.original?.email);
			const notesCount = Number(row.original.studentNotesOpenCount || 0);
			const notesPreview = Array.isArray(row.original.studentNotesOpenPreview)
				? row.original.studentNotesOpenPreview
				: [];
			return (
				<div className='flex w-full min-w-0 items-start gap-2'>
					<div className='min-w-0 flex-1 space-y-0.5'>
						<div className='flex min-w-0 items-center gap-2'>
							<p className='min-w-0 truncate text-sm font-semibold capitalize text-slate-800'>{name || '-'}</p>
							{notesCount > 0 && (
								<Tooltip
									variant='light'
									title={<StudentNotesPreview notes={notesPreview} totalCount={notesCount} />}
									placement='top'
								>
									<span
										role='button'
										tabIndex={0}
										className='inline-flex shrink-0 cursor-pointer items-center gap-1 rounded-full bg-gradient-to-br from-indigo-500 to-violet-600 px-2.5 py-1 text-xs font-semibold text-white shadow-md shadow-indigo-500/30 transition-transform duration-200 hover:-translate-y-0.5 hover:shadow-lg hover:shadow-indigo-500/40'
										aria-label={`Notas abiertas / en seguimiento: ${notesCount}`}
									>
										<ChatBubbleLeftEllipsisIcon className='h-3.5 w-3.5' aria-hidden='true' />
										<span className='leading-none tabular-nums'>{notesCount > 99 ? '99+' : notesCount}</span>
									</span>
								</Tooltip>
							)}
						</div>
						{email ? (
							<p className='truncate text-xs text-slate-500' title={email}>
								{email}
							</p>
						) : (
							<p className='truncate text-xs text-slate-400'>-</p>
						)}
					</div>
				</div>
			);
		},
		meta: {
			label: 'Estudiante',
			searchable: true,
			searchPlaceholder: 'Buscar nombre o correo...',
			excelHeaders: ['Nombre estudiante', 'Correo electronico'],
		},
	},
	{
		accessorKey: 'status',
		id: 'status',
		header: 'Estatus del estudiante',
		size: 160,
		cell: ({ getValue }) => (
			<div className='flex w-full justify-center'>
				<StatusBadge value={getValue()} />
			</div>
		),
		meta: { label: 'Estatus del estudiante', excelHeaders: ['Estatus del estudiante'] },
		enableSorting: false,
	},
	{
		accessorKey: 'statusSubscription',
		id: 'statusSubscription',
		header: 'Estatus de suscripción',
		size: 170,
		cell: ({ getValue }) => (
			<div className='flex w-full justify-center'>
				<SubscriptionBadge value={getValue()} />
			</div>
		),
		meta: { label: 'Estatus de suscripción', excelHeaders: ['Estatus de suscripcion'] },
		enableSorting: false,
	},
	{
		accessorKey: 'subscriptionPayment',
		id: 'subscriptionPayment',
		header: 'Monto de la suscripción',
		size: 180,
		cell: ({ getValue }) => {
			const value = getValue();
			if (!value) return null;
			return (
				<div className='flex w-full justify-center'>
					<p className='rounded-md bg-slate-100 px-3 py-1 text-center text-sm text-slate-700 capitalize'>{`$ ${value}`}</p>
				</div>		
			);
		},
		meta: { label: 'Monto de la suscripción', excelHeaders: ['Monto de la suscripcion'] },
	},
	{
		accessorKey: 'lastPaymentAmount',
		id: 'lastPaymentAmount',
		header: 'Monto del último pago',
		size: 180,
		cell: ({ getValue }) => {
			const value = getValue();
			if (!value) return null;
			return (
				<div className='flex w-full justify-center'>
					<p className='rounded-md bg-slate-100 px-3 py-1 text-center text-sm text-slate-700 capitalize'>{`$ ${value}`}</p>
				</div>
			);
		},
		meta: { label: 'Monto del último pago', excelHeaders: ['Monto del ultimo pago'] },
	},
	{
		accessorKey: 'paidWeeks',
		id: 'paidWeeks',
		header: 'Semanas pagadas',
		size: 150,
		cell: ({ getValue }) => {
			const value = getValue();
			if (!value) return null;
			return (
				<div className='flex w-full justify-center'>
					<p className='rounded-md bg-slate-100 px-3 py-1 text-center text-sm text-slate-700 capitalize'>{value}</p>
				</div>
			);
		},
		meta: { label: 'Semanas pagadas', excelHeaders: ['Semanas pagadas'] },
		enableSorting: false,
	},
	{
		accessorKey: 'lastPaymentType',
		id: 'lastPaymentType',
		header: 'Tipo del último pago',
		size: 150,
		cell: ({ getValue }) => {
			const value = normalizeValue(getValue()).toLowerCase();
			if (!value) return null;
			return (
				<div className='flex w-full justify-center'>
					<p className={`capitalize text-center px-3 py-1 rounded-md text-md font-bold ${value === 'suscripcion' ? 'bg-sky-100 text-sky-500' : 'bg-purple-100 text-purple-500'}`}>
						{value}
					</p>
				</div>
			);
		},
		meta: { label: 'Tipo del último pago', excelHeaders: ['Tipo del ultimo pago'] },
	},
	{
		id: 'paymentDeadlineDate',
		accessorKey: 'paymentDeadlineDate',
		header: () => (
			<div className='flex items-center gap-2'>
				<CalendarIcon className='h-4 w-4 text-gray-900' />
				<span>Fecha de último pago</span>
				<ArrowRightIcon className='h-4 w-4 text-gray-900' />
				<CalendarIcon className='h-4 w-4 text-rose-600' />
				<span>Fecha de vencimiento</span>
			</div>
		),
		size: 440,
		cell: ({ row }) => (
			<div className='flex items-center gap-2'>
				{row.original.lastDatePayment && (
					<p className='rounded-md bg-slate-100 px-3 py-1 text-center text-sm text-slate-700 capitalize'>
						{formatDate(row.original.lastDatePayment)}
					</p>
				)}
				{row.original.lastDatePayment && row.original.paymentDeadlineDate && (
					<span className='w-4'><ChevronRightIcon /></span>
				)}
				{row.original.paymentDeadlineDate && (
					<p className='rounded-md bg-slate-100 px-3 py-1 text-center text-sm text-slate-700 capitalize'>
						{formatDate(row.original.paymentDeadlineDate)}
					</p>
				)}
			</div>
		),
		meta: { label: 'Fechas de pago', excelHeaders: ['Fecha de ultimo pago', 'Fecha de vencimiento'] },
		enableSorting: false,
	},
	{
		id: 'progress',
		header: () => (
			<div className='flex items-center gap-2'>
				<ChartBarIcon className='h-4 w-4 text-green-600' />
				<span>Progreso de suscripción</span>
			</div>
		),
		enableSorting: false,
		size: 240,
		cell: ({ row }) => (
			<DateRangeProgressBarCell
				startDate={row.original.lastDatePayment}
				endDate={row.original.paymentDeadlineDate}
				allowMissingStart
				label={`Progreso de la suscripción: `}
			/>
		),
		meta: { label: 'Progreso de suscripción', enableExcel: false },
	},
	{
		accessorKey: 'phone',
		id: 'phone',
		header: 'Teléfono',
		size: 180,
		enableSorting: false,
		cell: ({ getValue }) => <p className='capitalize'>{normalizeValue(getValue())}</p>,
		meta: { label: 'Teléfono', searchable: true, searchPlaceholder: 'Buscar teléfono...', excelHeaders: ['Telefono'] },
	},
	{
		accessorKey: 'location',
		id: 'location',
		header: 'Ubicación',
		size: 220,
		cell: ({ getValue }) => <p className='capitalize'>{normalizeValue(getValue())}</p>,
		meta: { label: 'Ubicación', searchable: true, searchPlaceholder: 'Buscar ubicación...', excelHeaders: ['Ubicacion'] },
	},
	{
		accessorKey: 'createdAt',
		id: 'createdAt',
		header: () => (
			<div className='flex items-center gap-2'>
				<CalendarIcon className='h-4 w-4 text-gray-900' />
				<span>Fecha de inscripción</span>
			</div>
		),
		size: 230,
		cell: ({ getValue }) => (
			<p className='rounded-md bg-slate-100 px-3 py-1 text-center text-sm text-slate-700 capitalize'>
				{formatDate(getValue())}
			</p>
		),
		meta: { label: 'Fecha de inscripción del estudiante', excelHeaders: ['Fecha de inscripcion del estudiante'] },
	},
	{
		accessorKey: 'numCourses',
		id: 'numCourses',
		header: 'N. cursos inscritos',
		size: 140,
		cell: ({ getValue }) => (
			<div className='flex w-full justify-start'>
				<p className='rounded-md bg-slate-100 px-3 py-1 text-center text-sm text-slate-700 capitalize'>
					{normalizeValue(getValue())}
				</p>
			</div>
		),
		meta: { label: 'N. cursos inscritos', excelHeaders: ['N. cursos inscritos'] },
		enableSorting: false,
	},
	{
		accessorKey: 'courseIdentifiers',
		id: 'courseIdentifiers',
		header: 'Identificadores de cursos inscritos',
		enableSorting: false,
		size: 300,
		cell: ({ getValue }) => {
			const list = normalizeArray(getValue());
			return (
				<div className='flex flex-nowrap w-[300px] gap-2 overflow-x-auto whitespace-nowrap'>
					{list.map((item, i) => (
						<p
							key={`${item}-${i}`}
							className='rounded-md bg-slate-100 px-3 py-1 text-center text-sm text-slate-700 capitalize'
						>
							{item}
						</p>
					))}
				</div>
			);
		},
		meta: { label: 'Identificadores de cursos inscritos', searchable: true, searchPlaceholder: 'Buscar identificador...', excelHeaders: ['Identificadores de cursos inscritos'] },
	},
	{
		accessorKey: 'createdBy',
		id: 'createdBy',
		header: 'Inscrito por el usuario',
		size: 260,
		cell: ({ getValue }) => <p className='truncate text-sm font-semibold capitalize text-slate-700'>{normalizeValue(getValue())}</p>,
		meta: { label: 'Inscrito por el usuario', excelHeaders: ['Inscrito por el usuario'] },
	},
	{
		id: 'action',
		header: () => null,
		enableSorting: false,
		enableHiding: false,
		size: 170,
		minSize: 170,
		cell: ({ row }) => (
			<div className='flex w-full justify-center gap-2'>
				<Tooltip title='Ver perfil del estudiante' placement='left'>
					<button
						type='button'
						onClick={() => onOpenProfile(row.original.id)}
						className='inline-flex items-center justify-center rounded-md border border-gray-200 bg-gray-100 px-3 py-2 text-slate-700 transition-colors hover:bg-gray-200'
					>
						<UserIcon className='h-5 w-5' aria-hidden='true' />
						<span className='hidden md:inline ml-2 font-semibold text-md'>Ver perfil</span>
					</button>
				</Tooltip>
			</div>
		),
		meta: { label: 'Acciones', sticky: 'right' },
	},
];
